/**
 * PostalSlipsTab - Tab per riconciliazione bollettini postali
 *
 * Funzionalità:
 * - Import cartella con CSV + TIF scansioni
 * - OCR automatico delle immagini per estrarre causale, numero fattura e cliente
 * - Abbinamento automatico a fatture con suggerimenti multipli
 * - Dialog assegnazione manuale (come bonifici)
 * - Conferma/Ignora/Modifica manuale
 * - Preview immagine bollettino
 */
import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  Tooltip,
  LinearProgress,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Collapse,
  Divider,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  FolderOpen as FolderIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  Description as InvoiceIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
  AutoAwesome as AutoIcon,
  Refresh as RefreshIcon,
  Image as ImageIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Edit as EditIcon,
  Info as InfoIcon,
  AssignmentTurnedIn as AssignIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { invalidatePostalAccounting } from '../../utils/postalAccounting';
import { postalSlipsApi } from '../../services/api';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import PostalSlipAssignDialog from '../../components/payments/PostalSlipAssignDialog';
import PostalSlipMatchSummary, { confidenceLabel } from '../../components/payments/PostalSlipMatchSummary';
import { useAuth } from '../../context/AuthContext';
import { formatNuf } from '../../utils/formatNuf';
import { formatCustomerName } from '../../utils/formatCustomerName';

// Formatta valuta
const formatCurrency = (value) => {
  const num = parseFloat(value);
  if (isNaN(num)) return '€ 0,00';
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(num);
};

// Stati bollettino
const STATUS_LABELS = {
  pending: { label: 'In attesa', color: 'default' },
  ocr_done: { label: 'OCR eseguito', color: 'info' },
  suggested: { label: 'Suggerito', color: 'warning' },
  matched: { label: 'Abbinato', color: 'warning' },
  confirmed: { label: 'Confermato', color: 'success' },
  ignored: { label: 'Ignorato', color: 'default' },
  error: { label: 'Errore', color: 'error' },
};

export default function PostalSlipsTab() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();
  const { tenant } = useAuth();

  // Stati UI
  const [zipFile, setZipFile] = useState(null);
  const zipInputRef = React.useRef(null);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSearch, setFilterSearch] = useState('');
  const [sortBy, setSortBy] = useState('data_operazione');
  const [sortDir, setSortDir] = useState('desc');
  const [expandedRow, setExpandedRow] = useState(null);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingSlip, setEditingSlip] = useState(null);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assigningSlip, setAssigningSlip] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // Query batches
  const { data: batchesData, isLoading: batchesLoading } = useQuery({
    queryKey: ['postal-slips-batches'],
    queryFn: () => postalSlipsApi.getBatches().then(res => res.data),
  });

  const batches = batchesData?.data || [];
  const { data: integrityData } = useQuery({
    queryKey: ['postal-slips-integrity'],
    queryFn: () => postalSlipsApi.getIntegrity().then(res => res.data.data),
  });

  // Query bollettini del batch selezionato
  const { data: slipsData, isLoading: slipsLoading, refetch: refetchSlips } = useQuery({
    queryKey: ['postal-slips', selectedBatch],
    queryFn: () => postalSlipsApi.getAll({ batch_id: selectedBatch }).then(res => res.data),
    enabled: !!selectedBatch,
  });

  const allSlips = useMemo(() => slipsData?.data || [], [slipsData]);

  // Query statistiche
  const { data: statsData } = useQuery({
    queryKey: ['postal-slips-stats', selectedBatch],
    queryFn: () => postalSlipsApi.getStats({ batch_id: selectedBatch }).then(res => res.data),
    enabled: !!selectedBatch,
  });

  const stats = statsData?.data || {};

  // Filtra e ordina
  const slips = useMemo(() => {
    let filtered = [...allSlips];

    // Filtro stato
    if (filterStatus !== 'all') {
      filtered = filtered.filter(s => s.match_status === filterStatus);
    }

    // Filtro ricerca
    if (filterSearch) {
      const search = filterSearch.toLowerCase();
      filtered = filtered.filter(s =>
        (s.ocr_numero_fattura || '').toLowerCase().includes(search) ||
        (s.ocr_nome_cliente || '').toLowerCase().includes(search) ||
        (s.ocr_codice_cliente || '').toLowerCase().includes(search) ||
        (s.codice_riferimento || '').toLowerCase().includes(search) ||
        (s.accounting?.assignments || []).some(a =>
          (a.invoice ? formatNuf(a.invoice.sezionale, a.invoice.nuf) : '').toLowerCase().includes(search) ||
          formatCustomerName(a.customer, '').toLowerCase().includes(search) ||
          String(a.customer?.cod_cli || '').toLowerCase().includes(search))
      );
    }

    // Ordinamento
    filtered.sort((a, b) => {
      let cmp = 0;
      switch (sortBy) {
        case 'data_operazione':
          cmp = new Date(a.data_operazione || 0) - new Date(b.data_operazione || 0);
          break;
        case 'importo':
          cmp = parseFloat(a.importo || 0) - parseFloat(b.importo || 0);
          break;
        case 'ocr_confidence':
          cmp = (a.ocr_confidence || 0) - (b.ocr_confidence || 0);
          break;
        case 'match_status':
          const statusOrder = { pending: 0, ocr_done: 1, matched: 2, confirmed: 3, ignored: 4, error: 5 };
          cmp = (statusOrder[a.match_status] || 0) - (statusOrder[b.match_status] || 0);
          break;
        default:
          cmp = 0;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return filtered;
  }, [allSlips, filterStatus, filterSearch, sortBy, sortDir]);

  // Mutation import ZIP
  const importZipMutation = useMutation({
    mutationFn: (formData) => postalSlipsApi.uploadZip(formData),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['postal-slips-batches'] });

      // Mostra info duplicati se presenti
      const duplicatiEsclusi = res.data?.duplicati_esclusi || 0;
      if (duplicatiEsclusi > 0) {
        console.log(`⚠️ ${duplicatiEsclusi} bollettini duplicati esclusi dall'import`);
        const dettaglio = res.data?.duplicati_dettaglio || [];
        if (dettaglio.length > 0) {
          console.log('Dettaglio duplicati:', dettaglio);
        }
      }

      if (res.data?.batch_id) {
        setSelectedBatch(res.data.batch_id);
      }
      setZipFile(null);
      if (zipInputRef.current) zipInputRef.current.value = '';
    },
  });

  // Mutation elimina batch
  const deleteBatchMutation = useMutation({
    mutationFn: (batchId) => postalSlipsApi.deleteBatch(batchId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['postal-slips-batches'] });
      invalidatePostalAccounting(queryClient);
      queryClient.invalidateQueries({ queryKey: ['postal-slips-stats'] });
      if (selectedBatch) {
        setSelectedBatch(null);
      }
    },
  });

  // Mutation processo batch (OCR + matching)
  const processBatchMutation = useMutation({
    mutationFn: ({ batchId, reprocess = true }) => postalSlipsApi.processAll(batchId, reprocess),
    onSuccess: (response) => {
      console.log('✅ Process batch completato:', response.data);
      queryClient.invalidateQueries({ queryKey: ['postal-slips', selectedBatch] });
      queryClient.invalidateQueries({ queryKey: ['postal-slips-stats', selectedBatch] });
      queryClient.invalidateQueries({ queryKey: ['postal-slips-batches'] });
    },
  });

  // Mutation ignora singolo
  const ignoreMutation = useMutation({
    mutationFn: (id) => postalSlipsApi.ignore(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ['postal-slips', selectedBatch] });
      const previous = queryClient.getQueryData(['postal-slips', selectedBatch]);
      queryClient.setQueryData(['postal-slips', selectedBatch], (old) => {
        if (!old?.data) return old;
        return {
          ...old,
          data: old.data.map(s => s.id === id ? { ...s, match_status: 'ignored' } : s)
        };
      });
      return { previous };
    },
    onError: (err, id, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['postal-slips', selectedBatch], context.previous);
      }
    },
    onSettled: () => {
      invalidatePostalAccounting(queryClient);
      queryClient.invalidateQueries({ queryKey: ['postal-slips-stats'] });
    },
  });

  // Mutation aggiorna manualmente
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => postalSlipsApi.update(id, data),
    onSuccess: () => {
      setEditDialogOpen(false);
      setEditingSlip(null);
      invalidatePostalAccounting(queryClient);
    },
  });

  // Handlers
  const handleImportZip = () => {
    if (!zipFile) return;
    const formData = new FormData();
    formData.append('zipFile', zipFile);
    importZipMutation.mutate(formData);
  };

  const handleZipFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) setZipFile(file);
  };

  const handleProcessBatch = () => {
    if (!selectedBatch) return;
    // Passa reprocess=true per forzare rielaborazione di TUTTI gli slip, non solo pending
    processBatchMutation.mutate({ batchId: selectedBatch, reprocess: true });
  };

  const handleSortColumn = (column) => {
    if (sortBy === column) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDir(column === 'data_operazione' ? 'desc' : 'asc');
    }
  };

  const handleViewImage = (slip) => {
    setSelectedImage(slip);
    setImageDialogOpen(true);
  };

  const handleEdit = (slip) => {
    // In edit mode, usa il dialog di assegnazione per modificare le fatture assegnate
    setAssigningSlip(slip);
    setIsEditMode(true);
    setAssignDialogOpen(true);
  };

  const handleAssign = (slip) => {
    setAssigningSlip(slip);
    setIsEditMode(false);
    setAssignDialogOpen(true);
  };

  const handleCloseAssignDialog = () => {
    setAssignDialogOpen(false);
    setAssigningSlip(null);
    setIsEditMode(false);
  };

  const handleSaveEdit = () => {
    if (!editingSlip) return;
    updateMutation.mutate({
      id: editingSlip.id,
      data: {
        ocr_numero_fattura: editingSlip.ocr_numero_fattura,
        ocr_codice_cliente: editingSlip.ocr_codice_cliente,
        note: editingSlip.note,
      }
    });
  };

  // Handler per selezionare file ZIP
  const handleSelectZip = () => {
    if (zipInputRef.current) zipInputRef.current.click();
  };

  // Handler per eliminare un batch con conferma
  const handleDeleteBatch = (batchId) => {
    if (!batchId) {
      console.warn('Tentativo di eliminare batch con ID null/undefined');
      return;
    }
    if (window.confirm(`Sei sicuro di voler eliminare il batch "${batchId}"?\n\nTutti i bollettini importati verranno rimossi dal database.`)) {
      deleteBatchMutation.mutate(batchId);
    }
  };

  return (
    <Box>
      {integrityData?.count > 0 && <Alert severity="warning" sx={{ mb: 2 }}>
        {integrityData.count} bollettini confermati richiedono una verifica delle registrazioni contabili. Apri Modifica per controllare gli abbinamenti.
      </Alert>}
      {/* Sezione Import ZIP */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <CloudUploadIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Importa Bollettini Postali
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Carica un file ZIP contenente il CSV di Poste Italiane e le immagini TIF dei bollettini.
          </Typography>
          <input
            ref={zipInputRef}
            type="file"
            accept=".zip"
            style={{ display: 'none' }}
            onChange={handleZipFileChange}
          />
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', flexDirection: { xs: 'column', sm: 'row' } }}>
            <Button
              variant="outlined"
              onClick={handleSelectZip}
              startIcon={<FolderIcon />}
              sx={{ minWidth: 200 }}
            >
              {zipFile ? zipFile.name : 'Seleziona file ZIP...'}
            </Button>
            {zipFile && (
              <Typography variant="body2" color="text.secondary">
                {(zipFile.size / 1024 / 1024).toFixed(1)} MB
              </Typography>
            )}
            <Button
              variant="contained"
              onClick={handleImportZip}
              disabled={!zipFile || importZipMutation.isPending}
              startIcon={importZipMutation.isPending ? <CircularProgress size={20} /> : <CloudUploadIcon />}
            >
              {importZipMutation.isPending ? 'Caricamento...' : 'Importa'}
            </Button>
          </Box>
          {importZipMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Errore importazione: {importZipMutation.error?.response?.data?.message || importZipMutation.error?.message}
            </Alert>
          )}
          {importZipMutation.isSuccess && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {importZipMutation.data?.data?.message || `Importati ${importZipMutation.data?.data?.imported || 0} bollettini`}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Selezione Batch */}
      {batches.length > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>
              Seleziona Batch da lavorare:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              {batches.map((batch) => (
                <Box key={batch.batch_id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Chip
                    label={`${batch.batch_id} (${batch.total || batch.count} bollettini)`}
                    onClick={() => setSelectedBatch(batch.batch_id)}
                    color={selectedBatch === batch.batch_id ? 'primary' : 'default'}
                    variant={selectedBatch === batch.batch_id ? 'filled' : 'outlined'}
                  />
                  <Tooltip title="Elimina batch">
                    <span>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteBatch(batch.batch_id);
                        }}
                        disabled={deleteBatchMutation.isPending}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              ))}
            </Box>
            {deleteBatchMutation.isError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Errore eliminazione: {deleteBatchMutation.error?.response?.data?.message || deleteBatchMutation.error?.message}
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Contenuto batch selezionato */}
      {selectedBatch && (
        <>
          {/* Statistiche e azioni */}
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Stack direction="row" spacing={1}>
              <Chip
                label={`Totale: ${stats.total || 0}`}
                size="small"
              />
              <Chip
                label={`In attesa: ${stats.pending || 0}`}
                size="small"
                color="default"
              />
              <Chip
                label={`Abbinati: ${stats.matched || 0}`}
                size="small"
                color="warning"
              />
              <Chip
                label={`Confermati: ${stats.confirmed || 0}`}
                size="small"
                color="success"
              />
              <Chip
                label={`Errori: ${stats.error || 0}`}
                size="small"
                color="error"
              />
            </Stack>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                onClick={() => refetchSlips()}
                startIcon={<RefreshIcon />}
                size="small"
              >
                Aggiorna
              </Button>
              <Button
                variant="contained"
                onClick={handleProcessBatch}
                disabled={processBatchMutation.isPending}
                startIcon={processBatchMutation.isPending ? <CircularProgress size={20} /> : <AutoIcon />}
                color="secondary"
              >
                Elabora Tutti (OCR + Abbinamento)
              </Button>
            </Box>
          </Box>

          {processBatchMutation.isPending && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <LinearProgress sx={{ mt: 1 }} />
              Elaborazione OCR in corso... Questo potrebbe richiedere alcuni minuti.
            </Alert>
          )}

          {/* Filtri */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                placeholder="Cerca per n. fattura, cliente..."
                value={filterSearch}
                onChange={(e) => setFilterSearch(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>
                }}
                size="small"
                sx={{ minWidth: { xs: '100%', sm: 250 } }}
              />
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
                <InputLabel>Stato</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  label="Stato"
                >
                  <MenuItem value="all">Tutti</MenuItem>
                  {Object.entries(STATUS_LABELS).map(([value, { label }]) => (
                    <MenuItem key={value} value={value}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Paper>

          {/* Tabella bollettini */}
          {slipsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : slips.length === 0 ? (
            <Alert severity="info">
              {allSlips.length === 0
                ? 'Nessun bollettino in questo batch'
                : 'Nessun bollettino corrisponde ai filtri selezionati'
              }
            </Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.100' }}>
                    <TableCell width={40}></TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'data_operazione'}
                        direction={sortBy === 'data_operazione' ? sortDir : 'asc'}
                        onClick={() => handleSortColumn('data_operazione')}
                      >
                        Data
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={sortBy === 'importo'}
                        direction={sortBy === 'importo' ? sortDir : 'asc'}
                        onClick={() => handleSortColumn('importo')}
                      >
                        Importo
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ minWidth: { xs: 100, sm: 200 } }}>Dati OCR</TableCell>
                    <TableCell align="center" sx={{ minWidth: { xs: 100, sm: 220 }, display: { xs: 'none', md: 'table-cell' } }}>
                      Verifica Match
                    </TableCell>
                    <TableCell align="center">
                      <TableSortLabel
                        active={sortBy === 'match_status'}
                        direction={sortBy === 'match_status' ? sortDir : 'asc'}
                        onClick={() => handleSortColumn('match_status')}
                      >
                        Stato
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>Immagine</TableCell>
                    <TableCell align="right">Azioni</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {slips.map((slip) => {
                    // Parse suggestions
                    let suggestions = [];
                    try {
                      suggestions = slip.suggestions
                        ? (typeof slip.suggestions === 'string' ? JSON.parse(slip.suggestions) : slip.suggestions)
                        : [];
                    } catch {}

                    return (
                    <React.Fragment key={slip.id}>
                      <TableRow
                        hover
                        sx={{
                          '&:hover': { backgroundColor: 'action.hover' },
                          backgroundColor: slip.match_status === 'confirmed' ? 'success.50' :
                                          slip.match_status === 'ignored' ? 'grey.100' :
                                          slip.match_status === 'suggested' ? 'warning.50' :
                                          slip.match_status === 'error' ? 'error.50' : 'inherit'
                        }}
                      >
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => setExpandedRow(expandedRow === slip.id ? null : slip.id)}
                          >
                            {expandedRow === slip.id ? <CollapseIcon /> : <ExpandIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          {slip.data_operazione
                            ? format(new Date(slip.data_operazione), 'dd/MM/yyyy', { locale: it })
                            : '-'
                          }
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="medium" color="success.main">
                            {formatCurrency(slip.importo)}
                          </Typography>
                        </TableCell>
                        {/* Causale OCR - mostra solo Cliente e N. Fattura */}
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {slip.ocr_numero_fattura && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">Letto:</Typography>
                                <Typography variant="body2" fontWeight="medium">
                                  {slip.ocr_numero_fattura}
                                </Typography>
                              </Box>
                            )}
                            {slip.ocr_nome_cliente && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">Cliente:</Typography>
                                <Typography variant="body2" sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {slip.ocr_nome_cliente}
                                </Typography>
                              </Box>
                            )}
                            {slip.ocr_codice_cliente && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">Cod:</Typography>
                                <Typography variant="body2">
                                  {slip.ocr_codice_cliente}
                                </Typography>
                              </Box>
                            )}
                            {!slip.ocr_numero_fattura && !slip.ocr_nome_cliente && !slip.ocr_codice_cliente && (
                              <Typography variant="caption" color="text.secondary">-</Typography>
                            )}
                          </Box>
                        </TableCell>
                        {/* Verifica Match - 3 Chip: N fatt, Importo, Cliente */}
                        <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                          <PostalSlipMatchSummary slip={slip} />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={STATUS_LABELS[slip.match_status]?.label || slip.match_status}
                            size="small"
                            color={STATUS_LABELS[slip.match_status]?.color || 'default'}
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                          {slip.immagine_filename && (
                            <Tooltip title="Visualizza bollettino">
                              <IconButton size="small" onClick={() => handleViewImage(slip)}>
                                <ImageIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            {/* Assegna manualmente */}
                            {slip.match_status !== 'confirmed' && slip.match_status !== 'ignored' && (
                              <Tooltip title="Assegna manualmente">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleAssign(slip)}
                                >
                                  <AssignIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Modifica">
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(slip)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {slip.match_status !== 'confirmed' && slip.match_status !== 'ignored' && (
                              <>
                                <Tooltip title="Verifica bollettino e assegnazione">
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="success"
                                      onClick={() => handleAssign(slip)}
                                    >
                                      <CheckIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Ignora">
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="default"
                                      onClick={() => ignoreMutation.mutate(slip.id)}
                                      disabled={ignoreMutation.isPending}
                                    >
                                      <CancelIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>

                      {/* Riga espansa con dettagli */}
                      <TableRow>
                        <TableCell colSpan={8} sx={{ py: 0, borderBottom: expandedRow === slip.id ? 1 : 0 }}>
                          <Collapse in={expandedRow === slip.id}>
                            <Box sx={{ p: 2, backgroundColor: 'grey.50' }}>
                              <Grid container spacing={2}>
                                {/* Suggerimenti match */}
                                {slip.match_status !== 'confirmed' && suggestions.length > 0 && (
                                  <Grid item xs={12}>
                                    <Typography variant="subtitle2" gutterBottom color="primary">
                                      Suggerimenti Match ({suggestions.length})
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                      {suggestions.map((s, idx) => (
                                        <Paper
                                          key={idx}
                                          sx={{
                                            p: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            bgcolor: idx === 0 ? 'primary.50' : 'white',
                                            border: idx === 0 ? '2px solid' : '1px solid',
                                            borderColor: idx === 0 ? 'primary.main' : 'grey.300'
                                          }}
                                        >
                                          {s.tipo === 'fattura' ? (
                                            <>
                                              <InvoiceIcon fontSize="small" color="primary" />
                                              <Box>
                                                <Typography variant="body2" fontWeight="bold">
                                                  Fatt. {s.invoiceNum}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                  {s.customerName} - {formatCurrency(s.invoiceTotal)}
                                                </Typography>
                                              </Box>
                                            </>
                                          ) : (
                                            <>
                                              <PersonIcon fontSize="small" color="secondary" />
                                              <Box>
                                                <Typography variant="body2" fontWeight="bold">
                                                  Acconto
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                  {s.customerName}
                                                </Typography>
                                              </Box>
                                            </>
                                          )}
                                          <Chip
                                            label={`Affidabilità ${confidenceLabel(s).toLowerCase()}`}
                                            size="small"
                                            color={s.confidenceLevel === 'high' ? 'info' : 'warning'}
                                          />
                                        </Paper>
                                      ))}
                                    </Box>
                                    <Divider sx={{ my: 1 }} />
                                  </Grid>
                                )}
                                <Grid item xs={12} md={6}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Dati CSV (Poste Italiane)
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>Codice Riferimento:</strong> {slip.codice_riferimento || '-'}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>Data Accredito:</strong> {slip.data_accredito
                                      ? format(new Date(slip.data_accredito), 'dd/MM/yyyy', { locale: it })
                                      : '-'
                                    }
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>File Immagine:</strong> {slip.immagine_filename || '-'}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Dati OCR Estratti
                                  </Typography>
                                  {slip.ocr_causale && (
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                      <strong>Causale:</strong> {slip.ocr_causale}
                                    </Typography>
                                  )}
                                  <Typography variant="body2">
                                    <strong>Testo grezzo:</strong>
                                  </Typography>
                                  <Paper
                                    variant="outlined"
                                    sx={{
                                      p: 1,
                                      mt: 0.5,
                                      maxHeight: 100,
                                      overflow: 'auto',
                                      backgroundColor: 'white',
                                      fontFamily: 'monospace',
                                      fontSize: '0.75rem',
                                      whiteSpace: 'pre-wrap'
                                    }}
                                  >
                                    {slip.ocr_raw_text || 'OCR non ancora eseguito'}
                                  </Paper>
                                </Grid>
                                <Grid item xs={12}>
                                  <Divider sx={{ my: 1 }} />
                                  <PostalSlipMatchSummary slip={slip} detail />
                                </Grid>
                                {slip.ocr_error && (
                                  <Grid item xs={12}>
                                    <Alert severity="error" icon={<WarningIcon />}>
                                      {slip.ocr_error}
                                    </Alert>
                                  </Grid>
                                )}
                                {slip.note && (
                                  <Grid item xs={12}>
                                    <Typography variant="body2" color="text.secondary">
                                      <strong>Note:</strong> {slip.note}
                                    </Typography>
                                  </Grid>
                                )}
                              </Grid>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );})}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* Dialog visualizza immagine */}
      <Dialog
        open={imageDialogOpen}
        onClose={() => setImageDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              <ImageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Bollettino - {selectedImage?.immagine_filename}
            </Typography>
            <IconButton onClick={() => setImageDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedImage && (
            <Box sx={{ textAlign: 'center' }}>
              <img
                src={`/api/postal-slips/${selectedImage.id}/image${tenant?.slug ? '?tenant=' + tenant.slug : ''}`}
                alt="Bollettino"
                style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="200"><rect fill="%23f0f0f0" width="400" height="200"/><text x="50%" y="50%" font-family="Arial" font-size="16" fill="%23999" text-anchor="middle">Immagine non disponibile</text></svg>';
                }}
              />
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Data: {selectedImage.data_operazione
                    ? format(new Date(selectedImage.data_operazione), 'dd/MM/yyyy', { locale: it })
                    : '-'
                  } |
                  Importo: {formatCurrency(selectedImage.importo)}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog modifica */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Modifica Bollettino</DialogTitle>
        <DialogContent>
          {editingSlip && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Numero Fattura"
                  value={editingSlip.ocr_numero_fattura || ''}
                  onChange={(e) => setEditingSlip({ ...editingSlip, ocr_numero_fattura: e.target.value })}
                  helperText="Inserisci il numero fattura se l'OCR non l'ha riconosciuto"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Codice Cliente"
                  value={editingSlip.ocr_codice_cliente || ''}
                  onChange={(e) => setEditingSlip({ ...editingSlip, ocr_codice_cliente: e.target.value })}
                  helperText="Inserisci il codice cliente se l'OCR non l'ha riconosciuto"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Note"
                  value={editingSlip.note || ''}
                  onChange={(e) => setEditingSlip({ ...editingSlip, note: e.target.value })}
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Annulla</Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? <CircularProgress size={20} /> : 'Salva e Riabbina'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Info se nessun batch */}
      {batches.length === 0 && !batchesLoading && (
        <Alert severity="info" icon={<InfoIcon />}>
          Nessun batch di bollettini importato. Utilizza il form sopra per importare una cartella contenente
          il file CSV di Poste Italiane e le immagini TIF dei bollettini.
        </Alert>
      )}

      {/* Dialog assegnazione manuale */}
      <PostalSlipAssignDialog
        open={assignDialogOpen}
        onClose={handleCloseAssignDialog}
        slip={assigningSlip}
        queryClient={queryClient}
        onShowImage={handleViewImage}
        editMode={isEditMode}
      />
    </Box>
  );
}
